import { type AnyAction, type Dispatch, type Middleware, type MiddlewareAPI } from 'redux';
import { get } from 'lodash';
import { NOT_FOUND, redirect } from 'redux-first-router';
import { PAGES } from '../../utils/pages';
import { DASHBOARD_TABS } from '../../utils/constants';
import { getAppConfig } from '../selectors/appConfig';
import { isTabEnabledByRole } from '../../utils/mockedAppConfig';
import { getUser } from '../selectors/user';
import { type RootState } from '../types';

export const redirectRoutes: Middleware =
    (store: MiddlewareAPI<Dispatch<AnyAction>, RootState>) => (next: Dispatch<AnyAction>) => (action: AnyAction) => {
        const state = store.getState();

        const appConfig = getAppConfig(state);
        const user = getUser(state);

        if (!appConfig) {
            return next(action);
        }

        const salesDashboardOnly = appConfig.salesDashboardOnly;

        const isDashboardBlocked = !isTabEnabledByRole(
            user.locationId,
            user.companyId,
            user.role,
            'dashboard',
            salesDashboardOnly
        );

        const isCallHistoryBlocked = isTabEnabledByRole(
            user.locationId,
            user.companyId,
            user.role,
            'callHistory',
            salesDashboardOnly
        )
            ? !appConfig.callHistory
            : true;

        const isReportsBlocked = !isTabEnabledByRole(
            user.locationId,
            user.companyId,
            user.role,
            'reports',
            salesDashboardOnly
        );

        const isKeywordManagerBlocked = isTabEnabledByRole(
            user.locationId,
            user.companyId,
            user.role,
            'keywordManager',
            salesDashboardOnly
        )
            ? !appConfig.adminKeywordManager
            : true;

        const isUserManagerBlocked = isTabEnabledByRole(
            user.locationId,
            user.companyId,
            user.role,
            'userManager',
            salesDashboardOnly
        )
            ? !appConfig.adminUserManager
            : true;

        if (action.type === PAGES.CALL_HISTORY_NEW && isCallHistoryBlocked) {
            return next(redirect({ type: NOT_FOUND }));
        }
        if (action.type === PAGES.REPORTS && isReportsBlocked) {
            return next(redirect({ type: NOT_FOUND }));
        }
        if (action.type === PAGES.KEYWORD_MANAGER && isKeywordManagerBlocked) {
            return next(redirect({ type: NOT_FOUND }));
        }
        if (action.type === PAGES.USER_MANAGER && isUserManagerBlocked) {
            return next(redirect({ type: NOT_FOUND }));
        }
        if (action.type === PAGES.SALES && !appConfig.salesTab) {
            return next(redirect({ type: NOT_FOUND }));
        }
        if (action.type === PAGES.KPI_MANAGER && !appConfig.kpiManager) {
            return next(redirect({ type: NOT_FOUND }));
        }

        if (action.type === PAGES.DASHBOARD) {
            if (isDashboardBlocked) {
                if (appConfig.salesTab) {
                    return next(redirect({ type: PAGES.SALES }));
                }

                return next(redirect({ type: PAGES.CALL_HISTORY_NEW }));
            }

            const section = get(action, 'payload.section');

            if (!appConfig.dashboardShowI2XMetrics) {
                return next(redirect({ type: PAGES.REPORTS }));
            }
        }
        return next(action);
    };
